<script setup lang="ts">
import Marketplace from "../../Components/Algolia/Marketplace.vue";
import { Question, CuratedSearch } from "@/types";
import {
    onMounted,
    onBeforeUnmount,
    ref,
    onServerPrefetch,
    computed,
} from "vue";
import { Head } from "@inertiajs/vue3";

const props = defineProps({
    research: {
        type: String,
        required: false,
        default: "",
    },
    initialMakesFacet: {
        type: Array<{
            value: string;
            label: string;
            count: number;
            isRefined: boolean;
        }>,
        required: false,
        default: () => [],
    },
    fromVideo: {
        type: Boolean,
        default: false,
    },
    referenceInteriorColors: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    referenceExteriorColors: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    referenceInteriorMaterials: {
        type: Array,
        required: false,
        default: () => [],
    },
    faqMarketplace: {
        type: Array as () => Question[],
        required: false,
        default: () => [],
    },
    faqSchema: {
        type: String,
        required: false,
        default: null,
    },
    searchResultsPageSchema: {
        type: String,
        required: false,
        default: null,
    },
    autoDealerSchema: {
        type: Object,
        required: false,
        default: null,
    },
    bodyTypeLabels: {
        type: Object as () => Record<string, string>,
        required: false,
        default: () => ({}),
    },
    gearboxTypeLabels: {
        type: Object as () => Record<string, string>,
        required: false,
        default: () => ({}),
    },
    // Props pour les recherches curées
    curatedSearch: {
        type: Object as () => CuratedSearch | null,
        required: false,
        default: null,
    },
    marketplaceEquivalentUrl: {
        type: String,
        required: false,
        default: null,
    },
    canonicalUrl: {
        type: String,
        required: false,
        default: null,
    },
    metaTitle: {
        type: String,
        required: false,
        default: null,
    },
    metaDescription: {
        type: String,
        required: false,
        default: null,
    },
    breadcrumbs: {
        type: Array as () => Array<{ label: string; path?: string }>,
        required: false,
        default: null,
    },
    // Props pour les marques avec landing type marketplace
    vehicleMake: {
        type: Object as () => {
            designation: string;
            seo_intro?: string;
            seo_h1?: string;
            seo_subtitle?: string;
            logo_url?: string;
        } | null,
        required: false,
        default: null,
    },
    minMonthly: {
        type: Number,
        required: false,
        default: 0,
    },
});

const showOverlay = ref<boolean>(props.fromVideo);

onMounted(() => {
    showOverlay.value = false;
    if (typeof window !== "undefined") {
        document.documentElement.classList.add("full-container-page2");
    }
});

onBeforeUnmount(() => {
    if (typeof window !== "undefined") {
        document.documentElement.classList.remove("full-container-page2");
    }
});

const headerUrl = ref<string>(
    new URL(
        "../../../images/joinsteer-marketplace-header.webp",
        import.meta.url,
    ).href,
);

// Valeurs par défaut pour la marketplace standard
const defaultMetaDescription =
    "Trouvez votre voiture premium sur Joinsteer : leasing, LOA & LLD. 60 000 occasions certifiées, 11 marques. Livraison en France en 10 jours.";
const defaultMetaTitle =
    "Joinsteer Marketplace | Leasing (LOA/LLD) voitures d'occasion certifiées";

// Utiliser les meta de la recherche curée si disponibles
const effectiveMetaTitle = computed(() => props.metaTitle || defaultMetaTitle);
const effectiveMetaDescription = computed(
    () => props.metaDescription || defaultMetaDescription,
);

// Breadcrumb pour la page marketplace (utilisé si pas fourni par le backend)
const defaultBreadcrumbs = [
    { label: "Accueil", path: "/" },
    { label: "Marketplace" },
];

const effectiveBreadcrumbs = computed(
    () => props.breadcrumbs || defaultBreadcrumbs,
);

onServerPrefetch(async () => {
    // Do not remove this.
    // Algolia SSR should be activated here but there is still some work to do
    // For now all the work is done in the ssr.ts file
});
</script>

<template>
    <Head>
        <title>{{ effectiveMetaTitle }}</title>
        <meta
            head-key="description"
            name="description"
            :content="effectiveMetaDescription"
        />
        <meta property="og:image" :content="headerUrl" />
        <link rel="canonical" :href="canonicalUrl" />
        <component :is="'script'" v-if="faqSchema" type="application/ld+json">
            {{ faqSchema }}
        </component>
        <component
            :is="'script'"
            v-if="searchResultsPageSchema"
            type="application/ld+json"
        >
            {{ searchResultsPageSchema }}
        </component>
        <component
            :is="'script'"
            v-if="autoDealerSchema"
            type="application/ld+json"
        >
            {{ autoDealerSchema }}
        </component>
    </Head>

    <div class="vehicle-index">
        <Transition :name="fromVideo ? 'page' : ''">
            <div v-if="showOverlay" class="overlay"></div>
        </Transition>
        <Marketplace
            :research="research"
            :initial-makes-facet="initialMakesFacet"
            :reference-interior-colors="referenceInteriorColors"
            :reference-exterior-colors="referenceExteriorColors"
            :faq-marketplace="faqMarketplace"
            :breadcrumbs="effectiveBreadcrumbs"
            :curated-search="curatedSearch"
            :marketplace-equivalent-url="marketplaceEquivalentUrl"
            :vehicle-make="vehicleMake"
            :min-monthly="minMonthly"
            :body-type-labels="bodyTypeLabels"
            :gearbox-type-labels="gearboxTypeLabels"
        />
    </div>
</template>

<style lang="scss" scoped>
.overlay {
    position: fixed;
    width: 100vw;
    height: 100dvh;
    z-index: 1000;
    background-color: black;
}

.page-enter-active,
.page-leave-active {
    transition: all 2s 200ms;
}

.page-enter-from,
.page-leave-to {
    opacity: 0;
}
</style>
